/* eslint-disable @typescript-eslint/only-throw-error */
import {
  ConfirmRdcInput,
  Mutation,
  PresignedUpload,
  PresignedUploadFormField,
  Query,
  RdcCheckError,
  SubmitRdcInput,
  Typenames,
  UpdateRdcInput,
} from '@varo/graphql-schema-types';
import { File, Paths } from 'expo-file-system';
import uploadMultipartImage, {
  UploadMultipartImageResult,
} from 'features/rdc/screens/ReviewDeposit/hooks/uploadMultipartImage';
import { useRef, useState } from 'react';
import ConfirmCheckDeposit from 'services/graphql/mutations/MoveMoney/RDC/ConfirmCheckDeposit';
import PerformCheckDeposit from 'services/graphql/mutations/MoveMoney/RDC/PerformCheckDeposit';
import UpdateCheckDeposit from 'services/graphql/mutations/MoveMoney/RDC/UpdateCheckDeposit';
import FetchPresignedCheckDepositImageUpload from 'services/graphql/queries/MoveMoney/FetchPresignedCheckDepositImageUpload';
import useDefaultError from 'shared/hooks/errorFormatters/useDefaultError/useDefaultError';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import useCancellableQuery from 'shared/hooks/useCancellableQuery/useCancellableQuery';
import { isCombinedError } from 'shared/utils/errors/combinedErrorUtils';
import { CoreError, isCoreError } from 'shared/utils/errors/CoreError';
import { getUuid } from 'shared/utils/getUuid/getUuid';
import { useIdempotencyToken } from 'shared/utils/idempotencyTokens/idempotencyTokenUtils';
import {
  AnyVariables,
  CombinedError,
  OperationContext,
  OperationResult,
  RequestPolicy,
} from 'urql';

export enum DepositCheckStatus {
  SUCCESS = 'SUCCESS',
  FIXABLE = 'FIXABLE',
  CONFIRMABLE = 'CONFIRMABLE',
  REJECTED = 'REJECTED',
}

export interface PreSignedUploadForm {
  fileName: string;
  fileMimeType: string;
  uploadId: string;
  uploadURL: string;
  uploadName: string;
  uploadMultipartFormData: PresignedUploadFormField[];
}

export interface UploadFormElements {
  frontCheck?: PreSignedUploadForm;
  backCheck?: PreSignedUploadForm;
}

export interface StartDepositParams {
  session: string;
  accountId: string;
  amount: string;
  frontImageURI: string;
  backImageURI: string;
}

export interface SubmitDepositCheckError {
  depositCheckStatus: DepositCheckStatus;
  rdcUploadId?: string;
  errors: [string];
}

export interface SubmitDepositCheckSuccess {
  depositCheckStatus: DepositCheckStatus;
  confirmationNumber: string;
}

type EmptyVariables = Record<string, never>;
type SubmitRdcVariables = {
  idempotencyId: string;
  input: SubmitRdcInput;
};
type UpdateRdcVariables = {
  input: UpdateRdcInput;
};
type ConfirmRdcVariables = {
  idempotencyId: string;
  input: ConfirmRdcInput;
};
type ExecutePresignedUpload = (
  variables: EmptyVariables,
  context?: Partial<OperationContext>,
) => Promise<Query>;
type ExecuteMutation<Variables extends AnyVariables> = (
  variables: Variables,
  context?: Partial<OperationContext>,
) => Promise<OperationResult<Mutation, Variables>>;
type ExecuteSubmitRdc = ExecuteMutation<SubmitRdcVariables>;
type ExecuteUpdateRdc = ExecuteMutation<UpdateRdcVariables>;
type ExecuteConfirmRdc = ExecuteMutation<ConfirmRdcVariables>;

const fetchPresignedImageUploadFormData = async (
  executePresignedUpload: ExecutePresignedUpload,
  defaultError: CoreError,
  requestPolicy: RequestPolicy = 'network-only',
) => {
  const data = await executePresignedUpload({}, { requestPolicy }).catch(
    (error: unknown) => {
      if (error instanceof AbortError) {
        throw error;
      }

      throw defaultError;
    },
  );

  if (!data.presignedUpload) {
    throw defaultError;
  }

  const uploadFormElements = generateUploadFormElements(
    data.presignedUpload || [],
  );

  if (
    uploadFormElements?.frontCheck?.uploadURL &&
    uploadFormElements?.backCheck?.uploadURL
  ) {
    return uploadFormElements;
  }

  throw defaultError;
};

const generateUploadFormElements = (
  data: PresignedUpload[],
): UploadFormElements | null => {
  if (data.length < 2) {
    // Ensure there are at least two elements in the data array
    return null;
  }

  const [front, back] = data;

  const createUploadFormElement = (
    fileName: string,
    fileMimeType: string,
    uploadData: PresignedUpload,
  ) => ({
    fileName,
    fileMimeType,
    uploadId: uploadData.id,
    uploadURL: uploadData.uploadUrl,
    uploadName: uploadData.file,
    uploadMultipartFormData: uploadData.form,
  });

  return {
    frontCheck: createUploadFormElement('frontCheck.jpg', 'image/jpeg', front),
    backCheck: createUploadFormElement('backCheck.jpg', 'image/jpeg', back),
  };
};

const submitRdc = async (
  executeSubmitRdc: ExecuteSubmitRdc,
  idempotencyId: string,
  input: SubmitRdcInput,
  defaultError: CoreError,
  rotateToken: () => void,
): Promise<string> => {
  const result = await executeSubmitRdc({
    idempotencyId,
    input,
  });

  if (result.error || !result.data || !result.data?.submitRdc) {
    throw defaultError;
  }

  const response = result.data?.submitRdc;

  switch (response.__typename) {
    case Typenames.SubmitRdcSuccess:
      return response.id;
    case Typenames.SubmitRdcError:
      rotateToken();
      throw {
        depositCheckStatus: DepositCheckStatus.REJECTED,
        errors: [response.message],
      };
    case Typenames.SubmitRdcValidationError:
      rotateToken();
      throw handleValidationError(response.id, response.errors);
    default:
      throw defaultError;
  }
};

const updateCheckDeposit = async (
  executeUpdateRdc: ExecuteUpdateRdc,
  input: UpdateRdcInput,
  defaultError: CoreError,
): Promise<string> => {
  const result = await executeUpdateRdc({ input });

  if (result.error || !result.data || !result.data?.updateRdc) {
    throw defaultError;
  }

  const response = result.data?.updateRdc;
  switch (response.__typename) {
    case Typenames.UpdateRdcSuccess:
      return response.id;
    case Typenames.UpdateRdcError:
      throw {
        depositCheckStatus: DepositCheckStatus.REJECTED,
        errors: [response.message],
      };
    case Typenames.UpdateRdcValidationError:
      throw handleValidationError(response.id, response.errors);
    default:
      throw defaultError;
  }
};

const handleValidationError = (id: string, errors: RdcCheckError[]) => {
  const confirmableErrors = errors
    .filter((error) => error.isConfirmable)
    .map((error) => error.description);
  const fixableErrors = errors
    .filter((error) => error.isFixable)
    .map((error) => error.description);
  const otherErrors = errors
    .filter((error) => !error.isConfirmable && !error.isFixable)
    .map((error) => error.description);

  let depositCheckStatus: DepositCheckStatus;
  if (typeof otherErrors !== 'undefined' && otherErrors.length > 0) {
    depositCheckStatus = DepositCheckStatus.REJECTED;
  } else if (typeof fixableErrors !== 'undefined' && fixableErrors.length > 0) {
    depositCheckStatus = DepositCheckStatus.FIXABLE;
  } else if (
    typeof confirmableErrors !== 'undefined' &&
    confirmableErrors.length > 0
  ) {
    depositCheckStatus = DepositCheckStatus.CONFIRMABLE;
  } else {
    depositCheckStatus = DepositCheckStatus.REJECTED;
  }
  return {
    depositCheckStatus: depositCheckStatus,
    rdcUploadId: id,
    errors: [...confirmableErrors, ...fixableErrors, ...otherErrors],
  };
};

export const confirmCheckDeposit = async (
  executeConfirmRdc: ExecuteConfirmRdc,
  input: ConfirmRdcInput,
  defaultError: CoreError,
  idempotencyId: string,
  rotateToken: () => void,
) => {
  const result = await executeConfirmRdc({
    idempotencyId,
    input,
  });

  if (result.error || !result.data || !result.data?.confirmRdc) {
    throw defaultError;
  }

  const response = result.data?.confirmRdc;
  switch (response.__typename) {
    case Typenames.ConfirmRdcSuccess: {
      const confirmationNumber = response.confirmationNumber.startsWith('#')
        ? response.confirmationNumber
        : '#' + response.confirmationNumber;
      return {
        depositCheckStatus: DepositCheckStatus.SUCCESS,
        confirmationNumber: confirmationNumber,
      };
    }
    case Typenames.ConfirmRdcError:
      rotateToken();
      throw {
        depositCheckStatus: DepositCheckStatus.REJECTED,
        errors: [response.message],
      };
    case Typenames.ConfirmRdcCancelled:
      rotateToken();
      throw {
        depositCheckStatus: DepositCheckStatus.REJECTED,
        errors: [response.message],
      };
    default:
      throw defaultError;
  }
};

/**
 *  dataURI must be a data uri for a jpeg, with format:
 * `data:image/jpeg;base64,{IMAGE_DATA}`
 */
const writeTempBase64ImageFile = async (dataURI: string): Promise<File> => {
  const file = new File(Paths.cache, getUuid() + '.jpeg');

  // get image data portion of data uri.
  const data = dataURI.split(',')[1];
  if (!data) {
    throw new Error(
      'invalid data uri, please use format `data:image/jpeg;base64,{IMAGE_DATA}`',
    );
  }

  await file.write(data, { encoding: 'base64' });
  return file;
};

const cleanUpTempImageFileSlient = (file: File) => {
  try {
    file.delete();
  } catch {
    // do nothing
  }
};

export const generateFormData = (
  imageFile: File,
  presignedUploadFormData: PreSignedUploadForm,
) => {
  const formData = new FormData();

  presignedUploadFormData.uploadMultipartFormData.map((data) => {
    formData.append(data.name, data.value);
  });

  // Expo's fetch rejects the legacy {uri,type,fileName} form part; append the real File instead.
  formData.append(presignedUploadFormData.uploadName, imageFile);

  return formData;
};

// Type guards for error types
export const isSubmitDepositCheckError = (
  error: unknown,
): error is SubmitDepositCheckError => {
  return (error as SubmitDepositCheckError).depositCheckStatus !== undefined;
};

const submitOrUpdateCheckDeposit = async (
  executeSubmitRdc: ExecuteSubmitRdc,
  executeUpdateRdc: ExecuteUpdateRdc,
  params: StartDepositParams,
  presignedFormData: UploadFormElements,
  defaultError: CoreError,
  rdcUploadId: string | null,
  idempotencyToken: string,
  rotateToken: () => void,
) => {
  if (!presignedFormData.frontCheck || !presignedFormData.backCheck) {
    throw defaultError;
  }

  if (rdcUploadId) {
    // Update Deposit
    const updateRdcInput: UpdateRdcInput = {
      id: rdcUploadId,
      amount: {
        amount: params.amount,
        currency: 'USD',
      },
      frontImageId: presignedFormData.frontCheck.uploadId,
      backImageId: presignedFormData.backCheck.uploadId,
    };
    const rdcID = await updateCheckDeposit(
      executeUpdateRdc,
      updateRdcInput,
      defaultError,
    );
    return rdcID;
  }
  // Submit Deposit
  const input: SubmitRdcInput = {
    session: params.session,
    amount: {
      amount: params.amount,
      currency: 'USD',
    },
    accountId: params.accountId,
    frontImageId: presignedFormData.frontCheck.uploadId,
    backImageId: presignedFormData.backCheck.uploadId,
  };

  const rdcID = await submitRdc(
    executeSubmitRdc,
    idempotencyToken,
    input,
    defaultError,
    rotateToken,
  );
  return rdcID;
};

// Submit Check Deposit hook
const useSubmitCheckDeposit = (depositFlowId: string | number) => {
  const [executePresignedUpload] = useCancellableQuery<Query, EmptyVariables>(
    FetchPresignedCheckDepositImageUpload,
  );
  const [, executeSubmitRdc] = useCancellableMutation<
    Mutation,
    SubmitRdcVariables
  >(PerformCheckDeposit);
  const [, executeUpdateRdc] = useCancellableMutation<
    Mutation,
    UpdateRdcVariables
  >(UpdateCheckDeposit);
  const [, executeConfirmRdc] = useCancellableMutation<
    Mutation,
    ConfirmRdcVariables
  >(ConfirmCheckDeposit);
  // disable actions on this hook while one is in flight
  const isEnabledRef = useRef(true);
  // re-rendering show activity loading state
  const [isFetching, setIsFetching] = useState(false);

  const [rdcUploadId, setRDCUploadId] = useState<string | null>(null);
  const [uploadFormElements, setUploadFormElements] =
    useState<UploadFormElements | null>(null);
  const [submitDepositCheckResult, setSubmitDepositCheckResult] =
    useState<SubmitDepositCheckSuccess | null>(null);
  const [submitDepositCheckError, setSubmitDepositCheckError] = useState<
    SubmitDepositCheckError | CombinedError | CoreError | null
  >(null);
  const defaultError = useDefaultError();
  const { idempotencyToken: submitIdmToken, rotateToken: rotateSubmitToken } =
    useIdempotencyToken(depositFlowId);
  const { idempotencyToken: confirmIdmToken, rotateToken: rotateConfirmToken } =
    useIdempotencyToken(depositFlowId);

  const submitCheckDeposit = async (params: StartDepositParams) => {
    if (!isEnabledRef.current) {
      return;
    }

    let tempImageFiles: File[] = [];
    isEnabledRef.current = false;
    setIsFetching(true);
    setSubmitDepositCheckError(null);

    try {
      // Step 1: create presigned form data if we have not created it already
      let presignedFormData: UploadFormElements;
      if (!uploadFormElements) {
        const newPresignedFormData = await fetchPresignedImageUploadFormData(
          executePresignedUpload,
          defaultError,
        );

        // Save form data incase upload form interrupted
        setUploadFormElements(newPresignedFormData);
        presignedFormData = newPresignedFormData;
      } else {
        presignedFormData = uploadFormElements;
      }

      // Step 2: Upload check images
      if (!presignedFormData?.frontCheck || !presignedFormData.backCheck) {
        throw defaultError;
      }

      // we found a bug with image upload when we were using a data url for the image on
      // android. see this ticket: https://varomoney.atlassian.net/browse/JT-126457
      // some internal android form data behavior was changing the underlying image
      // format. we can mitigate this by writing the image to a temporary file.

      const [frontImageFile, backImageFile] = await Promise.all([
        writeTempBase64ImageFile(params.frontImageURI),
        writeTempBase64ImageFile(params.backImageURI),
      ]);
      tempImageFiles = [frontImageFile, backImageFile];

      const uploadResults = await Promise.all([
        uploadMultipartImage(
          presignedFormData.frontCheck.uploadURL,
          generateFormData(frontImageFile, presignedFormData.frontCheck),
        ),
        uploadMultipartImage(
          presignedFormData.backCheck.uploadURL,
          generateFormData(backImageFile, presignedFormData.backCheck),
        ),
      ]);

      // Check if uploads failed
      const isUploadOk = (result: UploadMultipartImageResult): boolean =>
        result.success;

      if (!isUploadOk(uploadResults[0]) || !isUploadOk(uploadResults[1])) {
        throw defaultError;
      }

      // Step 3: Submit/Update deposit
      const rdcID = await submitOrUpdateCheckDeposit(
        executeSubmitRdc,
        executeUpdateRdc,
        params,
        presignedFormData,
        defaultError,
        rdcUploadId,
        submitIdmToken,
        rotateSubmitToken,
      );

      // Step 4: Confirm Deposit and set result to state
      const input: ConfirmRdcInput = {
        rdcId: rdcID,
      };

      const confirmCheckDepositResult = await confirmCheckDeposit(
        executeConfirmRdc,
        input,
        defaultError,
        confirmIdmToken,
        rotateConfirmToken,
      );

      setSubmitDepositCheckResult(confirmCheckDepositResult);
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      } else if (isSubmitDepositCheckError(error)) {
        if (error?.rdcUploadId) {
          setRDCUploadId(error.rdcUploadId);
        }
        setSubmitDepositCheckError(error);
      } else if (isCombinedError(error)) {
        setSubmitDepositCheckError(error);
      } else if (isCoreError(error)) {
        setSubmitDepositCheckError(error);
      } else {
        // Handle unknown errors
        setSubmitDepositCheckError(defaultError);
      }
    } finally {
      // silently remove temp image files. the cache dir will be purged
      // periodically so failures are not an issue.
      tempImageFiles.forEach(cleanUpTempImageFileSlient);
      isEnabledRef.current = true;
      setIsFetching(false);
    }
  };

  const sendCheckAsIs = async (rdcId: string) => {
    if (!isEnabledRef.current) {
      return;
    }

    isEnabledRef.current = false;
    setIsFetching(true);
    try {
      const input: ConfirmRdcInput = {
        rdcId: rdcId,
      };
      const confirmCheckDepositResult = await confirmCheckDeposit(
        executeConfirmRdc,
        input,
        defaultError,
        confirmIdmToken,
        rotateConfirmToken,
      );

      if (!confirmCheckDepositResult) {
        throw defaultError;
      }

      setSubmitDepositCheckResult(confirmCheckDepositResult);
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      } else if (isSubmitDepositCheckError(error)) {
        if (error?.rdcUploadId) {
          setRDCUploadId(error.rdcUploadId);
        }
        setSubmitDepositCheckError(error);
      } else if (isCombinedError(error)) {
        setSubmitDepositCheckError(error);
      } else if (isCoreError(error)) {
        setSubmitDepositCheckError(error);
      } else {
        // Handle unknown errors
        setSubmitDepositCheckError(defaultError);
      }
    } finally {
      isEnabledRef.current = true;
      setIsFetching(false);
    }
  };

  return {
    isFetching,
    submitDepositCheckResult,
    submitDepositCheckError,
    submitCheckDeposit,
    sendCheckAsIs,
  };
};

export default useSubmitCheckDeposit;
